// src/useIsomorphicLayoutEffect.tsx
import * as React from "react";
var useIsomorphicLayoutEffect = typeof window !== "undefined" ? React.useLayoutEffect : () => {
};

// src/useInterval.ts
import { useEffect, useRef } from "react";
var useInterval = (callback, delay) => {
  const savedCallback = useRef(callback);
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);
  useEffect(() => {
    if (delay === null) {
      return;
    }
    const id = setInterval(() => savedCallback.current(), delay);
    return () => clearInterval(id);
  }, [delay]);
};

// src/useAsync.ts
import { useCallback, useEffect as useEffect2, useState } from "react";
var useAsync = (callback, dependencies = [], { condition } = { condition: true }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();
  const [value, setValue] = useState();
  const callbackMemoized = useCallback(() => {
    setLoading(true);
    setError(void 0);
    setValue(void 0);
    callback().then(setValue).catch(setError).finally(() => setLoading(false));
  }, dependencies);
  useEffect2(() => {
    if (!condition)
      return;
    callbackMemoized();
  }, [callbackMemoized]);
  return { loading, error, value };
};

// src/useOnClickOutside.ts
import { useEffect as useEffect3 } from "react";
var useOnClickOutside = (ref, handler) => {
  useEffect3(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target))
        return;
      handler(event);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
};

// src/useScript.ts
var useScript = (url, { condition } = { condition: true }) => {
  return useAsync(() => {
    const script = document.createElement("script");
    script.src = url;
    script.async = true;
    return new Promise((resolve, reject) => {
      script.addEventListener("load", resolve);
      script.addEventListener("error", reject);
      document.body.appendChild(script);
    });
  }, [url], { condition });
};

// src/useWindowSize.ts
import { useLayoutEffect as useLayoutEffect2, useState as useState2 } from "react";
var useWindowSize = () => {
  const [size, setSize] = useState2([0, 0]);
  useLayoutEffect2(() => {
    const updateSize = () => {
      setSize([window.innerWidth, window.innerHeight]);
    };
    window.addEventListener("resize", updateSize);
    updateSize();
    return () => window.removeEventListener("resize", updateSize);
  }, []);
  return size;
};

// src/useDetectMobile.ts
import { useEffect as useEffect4, useState as useState3 } from "react";
function detectMobile() {
  const toMatch = [/Android/i, /iPhone/i];
  return toMatch.some((toMatchItem) => {
    return navigator.userAgent.match(toMatchItem);
  });
}
function useDetectMobile() {
  const [isMobile, setMobile] = useState3();
  useEffect4(() => {
    setMobile(detectMobile());
  }, [detectMobile]);
  return { isMobile };
}

// src/useHideOnScroll.tsx
import {
  useState as useState4,
  useRef as useRef2,
  useEffect as useEffect5
} from "react";
var onScroll = ({ containerRef, setHide, topPosition }) => {
  if (containerRef.current) {
    const containerRefPostionTop = containerRef.current.getBoundingClientRect().top;
    if (containerRefPostionTop < topPosition) {
      setHide(true);
    } else {
      setHide(false);
    }
  }
};
var useHideOnScroll = ({ topPosition }) => {
  const [isHidden, setHide] = useState4(false);
  const containerRef = useRef2(null);
  useEffect5(() => {
    window.addEventListener("scroll", () => onScroll({
      containerRef,
      setHide,
      topPosition
    }));
    return () => window.removeEventListener("scroll", () => onScroll({
      containerRef,
      setHide,
      topPosition
    }));
  }, [containerRef]);
  return { isHidden, containerRef };
};
export {
  useAsync,
  useDetectMobile,
  useHideOnScroll,
  useInterval,
  useIsomorphicLayoutEffect,
  useOnClickOutside,
  useScript,
  useWindowSize
};
